import ModelFactory from 'business/models/ModelFactory';
import {
  AppInfoDTO,
  HomeClient,
  LicenseClient,
  LicenseDetailDTO,
} from 'business/models/Models';
import AutoUpdate from 'components/dashboard/AutoUpdate';
import Header from 'components/header/Header';
import { FmlxButton, FmlxCard, FmlxTable } from 'fmlx-common-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setCheckForUpgrade,
  setShowUpgradeFallbackDialog,
} from 'redux/reducers/commonReducer';
import { isAdministrator } from 'utils/Global';
import { SettingsTabsContext } from '../Settings/SettingsTabs';
import './LicenseStyle.scss';
import LicenseActivation from './modals/LicenseActivation';
import LicenseRequest from './modals/LicenseRequest';
import DateHelper from 'utils/DateHelper';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';

export const fmlxAdminUserName = 'FormulatrixAdmin';

const licenseClient: LicenseClient = ModelFactory(LicenseClient, false, true);
const homeClient: HomeClient = ModelFactory(HomeClient, false, true);

const hideFilter = false,
  hideSort = true,
  filterDefinition = { type: FmlxTable.FilterType.TEXT },
  concurrentColumns = [
    { Header: 'Edition', accessor: 'edition', hideSort, hideFilter, filterDefinition },
    { Header: 'Username', accessor: 'userName', hideSort, hideFilter, filterDefinition },
    { Header: 'Platform', accessor: 'platform', hideSort, hideFilter, filterDefinition },
    {
      Header: 'Last Activity',
      accessor: 'lastActive',
      hideSort,
      hideFilter,
      filterDefinition,
    },
  ];

function LicenseAdminSetup(props) {
  const [appInfo, setAppInfo] = useState<AppInfoDTO | null>(null);
  const [licenseDetail, setLicenseDetail] = useState<LicenseDetailDTO | null>(null);
  const [licenseList, setLicenseList] = useState<LicenseDetailDTO[]>([]);
  const { setHeaders } = React.useContext(SettingsTabsContext);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [sessionsFilter, setSessionListFilter] = useState<{ [key: string]: string }>({});
  const dispatch = useDispatch();

  useEffect(() => {
    async function fetchLicenseInfo() {
      const licenseDetailDto = await licenseClient.getLicenseDetail(true);

      updateAdminDataInState(licenseDetailDto);
      loadHybridLicense(licenseDetailDto);
    }

    async function fetchAboutAppInfo() {
      const appInfoDto = await homeClient.getAppInfo();
      if (appInfoDto) {
        setAppInfo(appInfoDto);
      }
    }

    fetchLicenseInfo();
    fetchAboutAppInfo();

    if (setHeaders) {
      setHeaders(
        <Header
          headerButtons={isAdministrator ? saveDiscardButtons : null}
          title={'License'}
          isNewTheme={true}
        />,
      );
    }
  }, []);

  function updateAdminDataInState(licenseDetailDto) {
    if (!licenseDetailDto) {
      return;
    }
    setLicenseDetail(licenseDetailDto);
  }

  async function loadHybridLicense(licenseDetailDto) {
    if (licenseDetailDto.licType === 'Hybrid') {
      const licList = await licenseClient.getHybridLicenseList(true);
      setLicenseList(licList);
    }
  }

  function closeModal() {
    setShowActivationModal(false);
    setShowRequestModal(false);
  }

  function requestLicenseClicked() {
    closeModal();
    setShowRequestModal(true);
  }

  function activateClicked() {
    closeModal();
    setShowActivationModal(true);
  }

  const sessionList = useMemo(() => {
    if (!licenseList?.length && !licenseDetail) {
      return [];
    }

    const source = licenseList?.length
      ? licenseList
      : licenseDetail
      ? [licenseDetail]
      : [];

    return source.flatMap((lic) =>
      (lic?.concurrentSessionList ?? []).map((session) => ({
        computerName: session.sessionName,
        userName: session.userName,
        lastActive: DateHelper.formatDateTime(session.sessionStartTime),
        platform: session.platform,
        edition: lic.edition,
      })),
    );
  }, [licenseDetail, licenseList]);

  const sessionListFiltered = useMemo(() => {
    return sessionList.filter((item) =>
      Object.entries(sessionsFilter).every(([key, value]) => {
        const filter = value?.trim().toLowerCase();
        if (!filter) {
          return true;
        }
        const itemValue = item[key];
        return itemValue?.toString().toLowerCase().includes(filter);
      }),
    );
  }, [sessionsFilter, sessionList]);

  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: false,
    onSave: () => {},
    onDiscard: () => {},
  });

  return (
    <>
      <AutoUpdate showFallback={false} />
      {showActivationModal && <LicenseActivation closeModal={closeModal} />}
      {showRequestModal && <LicenseRequest closeModal={closeModal} />}
      <div id="licenseadminsetup-wrapper" className="licenseContainer-wrapper">
        <div id="licenseadminsetup-container-row" className="licenseContainer p-2 row">
          <div
            id="licenseadminsetup-container-col-left"
            className="license-info-container col-md-6"
          >
            <FmlxCard
              id="licenseadminsetup-activation-card"
              selectable={false}
              disabled={false}
              draggable={false}
              variant={FmlxCard.Variant.OUTLINED}
              selected={false}
              type=""
            >
              <FmlxCard.Header id="licenseadminsetup-activation-card-header">
                Activation
              </FmlxCard.Header>
              <FmlxCard.Body
                id="licenseadminsetup-activation-card-body"
                className="license-activation"
              >
                <div id="licenseadminsetup-activation-content" className="flex-y-center">
                  <div id="licenseadminsetup-activation-content-license">License</div>
                </div>
                <div
                  id="licenseadminsetup-activation-buttons"
                  className="license-activation-buttons"
                >
                  <FmlxButton
                    id="licenseadminsetup-request-license-buttons"
                    type={FmlxButton.Type.BASIC}
                    onClick={requestLicenseClicked}
                    label="Request License"
                  />
                  <FmlxButton
                    id="licenseadminsetup-active-buttons"
                    type={FmlxButton.Type.BASIC}
                    onClick={activateClicked}
                    label="Activate"
                  />
                </div>
              </FmlxCard.Body>
            </FmlxCard>
            <FmlxCard
              id="licenseadminsetup-update-card"
              selectable={false}
              disabled={false}
              draggable={false}
              variant={FmlxCard.Variant.OUTLINED}
              selected={false}
              type=""
            >
              <FmlxCard.Header id="licenseadminsetup-update-header">
                Software Update
              </FmlxCard.Header>
              <FmlxCard.Body
                id="licenseadminsetup-update-body"
                className="flex-space-between"
              >
                <div id="licenseadminsetup-update-description">
                  Check Latest Software Update
                </div>
                <FmlxButton
                  id="licenseadminsetup-check-update-button"
                  type={FmlxButton.Type.BASIC}
                  onClick={() => {
                    dispatch(setCheckForUpgrade());
                    dispatch(setShowUpgradeFallbackDialog(true));
                  }}
                  label="Check Update"
                />
              </FmlxCard.Body>
            </FmlxCard>
            <LicenseStatus licenseDetail={licenseDetail} licenseList={licenseList} />
            <About appInfo={appInfo} />
          </div>
          {licenseDetail?.concurrentSessionList && (
            <div id="licenseadminsetup-container-col-right" className="col-md-6">
              <FmlxCard
                id="licenseadminsetup-session-card"
                selectable={false}
                disabled={false}
                draggable={false}
                variant={FmlxCard.Variant.OUTLINED}
                selected={false}
                type=""
              >
                <FmlxCard.Header id="licenseadminsetup-session-list-header">
                  List of Concurrent Sessions
                </FmlxCard.Header>
                <FmlxCard.Body id="licenseadminsetup-session-list-body">
                  <div
                    id="licenseadminsetup-session-list-description"
                    className="session-list-container"
                  >
                    <FmlxTable
                      id="licenseadminsetup-active-session-list-button"
                      columns={concurrentColumns as any}
                      data={sessionListFiltered}
                      allowSelectedRow={false}
                      showFilter
                      getFilterMap={(filter) => setSessionListFilter(filter as any)}
                      isLoading={false}
                      placeholder={null}
                    />
                  </div>
                </FmlxCard.Body>
              </FmlxCard>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const LicenseStatus: React.FC<{
  licenseDetail: LicenseDetailDTO | null;
  licenseList: LicenseDetailDTO[] | null;
}> = ({ licenseDetail, licenseList }) => {
  if (!licenseDetail) {
    return null;
  }

  const licenseTypeInfo = React.useMemo(() => {
    if (licenseDetail.licType == 'Hybrid') {
      return licenseList.map((l, i) => <li key={`li${i}`}>{l.title}</li>);
    } else {
      return licenseDetail.title;
    }
  }, [licenseDetail, licenseList]);

  const licenseFeatures = React.useMemo(() => {
    const { licFeatures, licType } = licenseDetail;
    if (licFeatures != null && licFeatures.length > 0) {
      return licFeatures.map((f, i) => (
        <li id={`licenseadminsetup-license-status-feature-item-${i}`} key={`li${i}`}>
          {f}
        </li>
      ));
    } else {
      if (licType == 'Temporary' || licType == 'Trial') {
        return 'All';
      } else {
        return 'None';
      }
    }
  }, [licenseDetail]);

  return (
    <FmlxCard
      id="licenseadminsetup-license-status-wrapper"
      selectable={false}
      disabled={false}
      draggable={false}
      variant={FmlxCard.Variant.OUTLINED}
      selected={false}
      type=""
    >
      <FmlxCard.Header id="licenseadminsetup-license-status-header">
        <div id="licenseadminsetup-license-status-description">
          License Status
          {licenseDetail.isActive && (
            <span id="licenseadminsetup-license-status-active" className="text-success">
              (Active)
            </span>
          )}
        </div>
      </FmlxCard.Header>
      <FmlxCard.Body id="licenseadminsetup-license-status-card-body" className="row">
        <div
          id="licenseadminsetup-license-status-left-column"
          className="col-6 license-info"
        >
          <Group title="Licensed To">
            <div
              id={`licenseadminsetup-license-status-licensedto-${licenseDetail.licensedToName}`}
            >
              {licenseDetail.licensedToName}
            </div>
            <div
              id={`licenseadminsetup-license-status-licensedto-${licenseDetail.licensedToEmail}`}
            >
              {licenseDetail.licensedToEmail}
            </div>
            <div
              id={`licenseadminsetup-license-status-licensedto-${licenseDetail.clientId}`}
            >
              {licenseDetail.clientId}
            </div>
          </Group>
          <Group title="Licensed Product">
            <div
              id={`licenseadminsetup-license-status-licensedto-${licenseDetail.licProduct?.name?.toUpperCase()}`}
            >
              {licenseDetail.licProduct?.name?.toUpperCase()}
            </div>
          </Group>
          <Group title="Maintenance Expiration Date">{licenseDetail.expiryDate}</Group>
        </div>
        <div
          id="licenseadminsetup-license-status-right-column"
          className="col-6 license-info"
        >
          <Group title="Licensed feature(s)">{licenseFeatures}</Group>
          <Group title="License type (s)">
            <div id="licenseadminsetup-license-status-licensetype-value">
              {licenseTypeInfo}
            </div>
          </Group>
        </div>
      </FmlxCard.Body>
    </FmlxCard>
  );
};

const About: React.FC<{ appInfo: AppInfoDTO | null }> = ({ appInfo }) => {
  if (!appInfo) {
    return null;
  }

  const dateString = useMemo(
    () => new Date(appInfo.buildDate).toLocaleDateString('en-US'),
    [appInfo],
  );
  const version = `ROCK MAKER® v${appInfo.version}`;

  return (
    <FmlxCard
      id="licenseadminsetup-about-card-wrapper"
      selectable={false}
      disabled={false}
      draggable={false}
      variant={FmlxCard.Variant.OUTLINED}
      selected={false}
      type=""
    >
      <FmlxCard.Header id="licenseadminsetup-about-header">About</FmlxCard.Header>
      <FmlxCard.Body id="licenseadminsetup-about-body" className="row">
        <div id="licenseadminsetup-about-title-section" className="col-6">
          <Group title="Version">
            {version}
            <br />
            {dateString}
          </Group>
        </div>
        <div id="licenseadminsetup-about-support-section" className="col-6">
          <Group title="Support">
            {appInfo.supportPhone}
            <br />
            {appInfo.supportEmail}
            <br />
            Copyright {appInfo.copyRight} {appInfo.companyName}
          </Group>
        </div>
      </FmlxCard.Body>
    </FmlxCard>
  );
};

const Group: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <div id="licenseadminsetup-group-wrapper">
      <div id={`licenseadminsetup-group-${title}`}>{title}</div>
      <div
        id={`licenseadminsetup-group-content-${children}`}
        className="group-subgroup ml-3"
      >
        {children}
      </div>
    </div>
  );
};

export default LicenseAdminSetup;
